// Build-A-Drip Product Configurator
// Debug mode - set to true to enable console logs
const DEBUG_MODE = true;

function initBuildADrip() {
  if (DEBUG_MODE) console.log("Build-A-Drip configurator initializing...");

  function debounce(func, delay) {
    let timeout;
    return () => {
      clearTimeout(timeout);
      timeout = setTimeout(func, delay);
    };
  }

  window.addEventListener("load", setupAccordions);
  window.addEventListener("resize", debounce(setupAccordions, 200));

  // Initialize the 3D model viewer directly since we're no longer in a modal
  const container = document.getElementById("build-a-drip-model-viewer");
  const modelLoader = document.getElementById("model-loader");

  // Show loading overlay initially
  if (modelLoader) {
    modelLoader.style.display = "flex";
  }

  // Main class to handle the product configurator
  class BuildADripConfigurator {
    constructor() {
      // Elements
      this.modelViewer = document.getElementById("build-a-drip-model-viewer");
      this.priceElement = document.getElementById("build-a-drip-price");
      this.priceElementMobile = document.getElementById(
        "build-a-drip-price-mobile",
      );
      this.addToCartBtn = document.getElementById("add-to-cart");
      this.mobileAddToCartBtn = document.querySelector(".mobile-add-to-cart");
      this.shareBtn = document.getElementById("share-config");
      this.shareBtnMobile = document.getElementById("share-config-mobile");

      // Options containers
      this.baseColorOptions = document.getElementById("base-color-options");
      this.carabinerColorOptions = document.getElementById(
        "carabiner-color-options",
      );
      this.charmOptions = document.getElementById("charm-options");

      // Mobile elements
      this.mobileOptionsWrapper = document.querySelector(
        ".mobile-options-wrapper",
      );
      this.mobileSelectedOptionDetails = document.querySelector(
        ".mobile-selected-option-details",
      );
      this.prevBtn = document.querySelector(".prev-btn");
      this.nextBtn = document.querySelector(".next-btn");
      this.currentStepElement = document.querySelector(".current-step");

      // Data
      this.basePrice = parseFloat(
        document.querySelector("#build-a-drip-base-price").dataset.price,
      );
      const buildADripSection = document.getElementById(
        "build-a-drip-container",
      );
      this.showNoCarabiner =
        buildADripSection?.dataset.showNoCarabiner === "true";
      this.currentOptions = {
        beads: null,
        carabiner: null,
        charm: null,
      };

      this.productsData = {
        beads: [],
        carabiner: [],
        charm: [],
      };

      // Base type: 'beads' or 'metal'
      this.baseType = 'beads';
      this.beadProducts = [];
      this.chainProducts = [];

      // Mobile navigation state
      this.currentMobileStep = 1;
      this.totalMobileSteps = 4;

      this.modelLoaded = false;

      // Necklace-specific carabiner overrides
      // This allows certain necklaces to show different carabiner nodes
      this.necklaceCarabinerOverrides = {
        sonny: "Carabiner - Gold",
        sir: "NECKLACE_BEAN001",
        // Add more overrides as needed:
        // "other-necklace": "Carabiner - Silver",
      };

      // Initialize
      this.init();
    }

    async init() {
      if (DEBUG_MODE) console.log("BuildADripConfigurator initializing...");
      await this.fetchBuildADripProducts();
      this.parseUrlParams();
      this.renderOptions();
      this.setupEventListeners();
      if (DEBUG_MODE)
        console.log("BuildADripConfigurator initialization complete");
    }

    // Fetch products from the Build A Drip collection
    async fetchBuildADripProducts() {
      try {
        const variantMetafieldsScript = document.getElementById(
          "build-a-drip-variants",
        );
        let variantMetafields = {};

        if (variantMetafieldsScript) {
          try {
            variantMetafields = JSON.parse(variantMetafieldsScript.textContent);
          } catch (e) {
            console.error("Failed to parse variant metafields JSON:", e);
          }
        }

        const response = await fetch(
          "/collections/9f31b47a-dc2d-4c3e-b876-a3fc8e7e7e11/products.json",
        );
        const data = await response.json();

        data.products.forEach((product) => {
          const productType = this.getProductType(product);
          if (!productType) return;

          product.variants.forEach((variant) => {
            const variantId = String(variant.id);
            const meta = variantMetafields[variantId] || {};

            // Skip options that have hidden_on_new_site metafield (unless on local dev)
            const isLocalEnv =
              window.location.hostname === "localhost" ||
              window.location.hostname === "127.0.0.1";
            if (
              !isLocalEnv &&
              (meta.hidden_on_new_site == true ||
                meta.hidden_on_new_site === "true")
            ) {
              return;
            }

            const nodeId = meta.node_id || "";
            const colorName = meta.color_name || variant.title;
            const thumbImage =
              meta.image ||
              (variant.featured_image ? variant.featured_image.src : null);
            const otherThumbImage = meta.other_thumb
              ? `https:${meta.other_thumb}`
              : null;

            const option = {
              id: variant.id,
              productId: product.id,
              title: variant.title,
              colorName: colorName,
              nodeId: this.normalizeNodeId(nodeId),
              price: parseFloat(variant.price),
              image: thumbImage,
              otherThumbImage: otherThumbImage,
              available: variant.available,
              sku: variant.sku,
            };

            this.productsData[productType].push(option);
          });
        });
        const noCharmImageUrl =
          "https://cdn.shopify.com/s/files/1/0767/9272/5688/files/no-charm.png?v=1743305046";
        this.productsData.charm.unshift({
          id: "no-charm",
          productId: null,
          title: "No Charm",
          colorName: "No Charm",
          nodeId: null,
          price: 0,
          image: noCharmImageUrl,
          otherThumbImage: null,
          available: true,
          sku: null,
        });

        // Only add "no carabiner" option if enabled in settings
        if (this.showNoCarabiner) {
          const noCarabinerImageUrl =
            "https://cdn.shopify.com/s/files/1/0767/9272/5688/files/no-charm.png?v=1743305046";
          this.productsData.carabiner.unshift({
            id: "no-carabiner",
            productId: null,
            title: "No Carabiner",
            colorName: "No Carabiner",
            nodeId: null,
            price: 0,
            image: noCarabinerImageUrl,
            otherThumbImage: null,
            available: true,
            sku: null,
          });
        }
      } catch (error) {
        console.error("Error fetching Build-A-Drip products:", error);
      }
    }

    // Determine the product type for categorization
    getProductType(product) {
      if (product.tags && product.tags.includes("beads")) return "beads";
      if (product.tags && product.tags.includes("carabiner"))
        return "carabiner";
      if (product.tags && product.tags.includes("charm")) return "charm";

      const title = product.title.toLowerCase();
      if (title.includes("bead")) return "beads";
      if (title.includes("carabiner")) return "carabiner";
      if (title.includes("charm")) return "charm";

      return null;
    }

    // Parse URL parameters to auto-select options
    parseUrlParams() {
      const urlParams = new URLSearchParams(window.location.search);

      const beadParam = urlParams.get("bead");
      const carabinerParam = urlParams.get("carabiner");
      const charmParam = urlParams.get("charm");

      // Find bead by ID or SKU (search all beads including chains)
      const beadOption = beadParam
        ? this.productsData.beads.find(
            (opt) => opt.id.toString() === beadParam || opt.sku === beadParam,
          )
        : null;

      // Auto-detect base type from matched product
      if (beadOption && beadOption.nodeId && beadOption.nodeId.startsWith("chain")) {
        this.baseType = "metal";
      }

      this.currentOptions.beads = beadOption || this.productsData.beads[0];

      // Find carabiner by ID or SKU
      const carabinerOption = carabinerParam
        ? this.productsData.carabiner.find(
            (opt) =>
              opt.id.toString() === carabinerParam ||
              opt.sku === carabinerParam,
          )
        : null;
      this.currentOptions.carabiner =
        carabinerOption ||
        this.productsData.carabiner.find((opt) => opt.colorName === "Gold" && opt.available) ||
        this.productsData.carabiner.find((opt) => opt.id !== "no-carabiner" && opt.available) ||
        this.productsData.carabiner[0];

      // Find charm by ID or SKU
      if (charmParam) {
        const charmOption = this.productsData.charm.find(
          (opt) => opt.id.toString() === charmParam || opt.sku === charmParam,
        );
        if (charmOption) this.currentOptions.charm = charmOption;
      } else {
        this.currentOptions.charm = this.productsData.charm.find(
          (opt) => opt.id === "no-charm",
        );
      }
    }

    // Create option selectors in the UI
    renderOptions() {
      // Split beads into bead vs chain products by node ID prefix
      this.beadProducts = this.productsData.beads.filter(
        (opt) => !opt.nodeId || !opt.nodeId.startsWith("chain"),
      );
      this.chainProducts = this.productsData.beads.filter(
        (opt) => opt.nodeId && opt.nodeId.startsWith("chain"),
      );

      // Render desktop bead options
      this.renderProductOptions(
        this.baseColorOptions,
        this.beadProducts,
        "beads",
        true,
      );

      // Render desktop chain options
      const baseChainOptions = document.getElementById("base-chain-options");
      if (baseChainOptions) {
        this.renderProductOptions(
          baseChainOptions,
          this.chainProducts,
          "beads",
          true,
        );
      }

      this.renderProductOptions(
        this.carabinerColorOptions,
        this.productsData.carabiner,
        "carabiner",
        true,
      );

      this.renderProductOptions(
        this.charmOptions,
        this.productsData.charm,
        "charm",
        false,
      );

      // Render mobile options
      const mobileBaseColorOptions = document.getElementById(
        "base-color-options-mobile",
      );
      const mobileBaseChainOptions = document.getElementById(
        "base-chain-options-mobile",
      );
      const mobileCarabinerColorOptions = document.getElementById(
        "carabiner-color-options-mobile",
      );
      const mobileCharmOptions = document.getElementById(
        "charm-options-mobile",
      );

      if (mobileBaseColorOptions) {
        this.renderProductOptions(
          mobileBaseColorOptions,
          this.beadProducts,
          "beads",
          true,
        );
      }

      if (mobileBaseChainOptions) {
        this.renderProductOptions(
          mobileBaseChainOptions,
          this.chainProducts,
          "beads",
          true,
        );
      }

      if (mobileCarabinerColorOptions) {
        this.renderProductOptions(
          mobileCarabinerColorOptions,
          this.productsData.carabiner,
          "carabiner",
          true,
        );
      }

      if (mobileCharmOptions) {
        this.renderProductOptions(
          mobileCharmOptions,
          this.productsData.charm,
          "charm",
          false,
        );
      }

      this.updateUISelections();
      this.updateTotalPrice();

      // Apply initial base type visibility
      this.applyBaseTypeVisibility();
    }

    // Apply base type visibility without changing selection
    applyBaseTypeVisibility() {
      const isBeads = this.baseType === 'beads';

      // Update toggle buttons (desktop + mobile)
      document.querySelectorAll('.base-type-toggle-btn').forEach((btn) => {
        btn.classList.toggle('active', btn.dataset.baseType === this.baseType);
      });

      // Desktop: show/hide beads vs chain option groups
      const beadsGroup = document.querySelector('[data-option-group="beads"]');
      const chainGroup = document.querySelector('[data-option-group="chain"]');
      if (beadsGroup) beadsGroup.style.display = isBeads ? '' : 'none';
      if (chainGroup) chainGroup.style.display = isBeads ? 'none' : '';

      // Mobile: show/hide beads vs chain option groups
      const mobileBeadsGroup = document.querySelector('[data-mobile-option-group="beads"]');
      const mobileChainGroup = document.querySelector('[data-mobile-option-group="chain"]');
      if (mobileBeadsGroup) mobileBeadsGroup.style.display = isBeads ? '' : 'none';
      if (mobileChainGroup) mobileChainGroup.style.display = isBeads ? 'none' : '';
    }

    // Switch between beads and metal base types
    setBaseType(type) {
      this.baseType = type;
      this.applyBaseTypeVisibility();

      // Auto-select first available option from the new category
      const pool = type === 'beads' ? this.beadProducts : this.chainProducts;
      if (pool.length > 0) {
        const firstAvailable = pool.find((opt) => opt.available) || pool[0];
        this.selectOption('beads', firstAvailable);
      }

      this.updateShareableUrl();
    }

    // Render product options
    renderProductOptions(container, options, optionType, isColorOption) {
      container.innerHTML = "";

      // Sort options: available first, sold out last
      const sortedOptions = [...options].sort((a, b) => {
        // Available options come first (true = -1), sold out last (false = 1)
        if (a.available && !b.available) return -1;
        if (!a.available && b.available) return 1;
        return 0; // Keep original order for items with same availability
      });

      console.log("Carabiner options:", sortedOptions);
      sortedOptions.forEach((option) => {
        // if (!option.available) return;

        const optionElement = document.createElement("div");
        optionElement.className = isColorOption
          ? "option-item color-option"
          : "option-item charm-option";
        optionElement.dataset.optionId = option.id;
        optionElement.dataset.optionType = optionType;
        optionElement.dataset.nodeId = option.nodeId;

        // Out of stock
        if (!option.available) {
          optionElement.classList.add("option-unavailable");

          // Add sold out indicator
          const soldOutLabel = document.createElement("div");
          soldOutLabel.className = "sold-out-label";
          soldOutLabel.textContent = "Sold Out";
          optionElement.appendChild(soldOutLabel);
        }

        if (isColorOption) {
          const isHeartOption = option.colorName
            .toLowerCase()
            .includes("heart");

          if (option.image) {
            const imgElement = document.createElement("img");
            imgElement.src = option.otherThumbImage || option.image;
            imgElement.alt = option.colorName || option.title;
            imgElement.className = "color-option-image";
            imgElement.loading = "lazy";
            imgElement.decoding = "async";

            if (isHeartOption) {
              // Heart option: no wrapper
              optionElement.appendChild(imgElement);
            } else {
              // Other color options: use wrapper
              // Check if this is a mobile container (40px) or desktop (34px)
              const isMobile = container.id && container.id.includes("-mobile");
              const wrapperSize = isMobile ? "40px" : "34px";

              const imgWrapper = document.createElement("div");
              imgWrapper.style.width = wrapperSize;
              imgWrapper.style.height = wrapperSize;
              imgWrapper.style.overflow = "hidden";
              imgWrapper.style.borderRadius = "50%";
              imgWrapper.appendChild(imgElement);
              optionElement.appendChild(imgWrapper);
            }
          }
          if (isHeartOption) {
            optionElement.classList.add("heart-option");
          }

          optionElement.title = option.colorName || option.title;
        } else {
          if (option.id === "no-charm") {
            const imgElement = document.createElement("img");
            imgElement.src = option.image;
            imgElement.alt = "no charm";
            imgElement.className = "charm-image";
            imgElement.loading = "lazy";
            imgElement.decoding = "async";
            optionElement.prepend(imgElement);
          } else {
            const displayName = option.colorName || option.title;
            if (option.image) {
              const imgElement = document.createElement("img");
              imgElement.src = option.image;
              imgElement.alt = displayName;
              imgElement.className = "charm-image";
              imgElement.loading = "lazy";
              imgElement.decoding = "async";
              optionElement.prepend(imgElement);
            }
          }
        }

        // Check if this option is pre-selected from URL
        if (
          this.currentOptions[optionType] &&
          this.currentOptions[optionType].id === option.id
        ) {
          optionElement.classList.add("selected");
        }

        if (window.innerWidth >= 1025) {
          // Determine the correct name display ID based on container
          const isChainContainer = container.id && container.id.includes("chain");
          const hoverNameDisplayId = (optionType === "beads" && isChainContainer)
            ? "chain-selected-name"
            : `${optionType}-selected-name`;

          optionElement.addEventListener("mouseenter", () => {
            const nameDisplay = document.getElementById(hoverNameDisplayId);
            if (nameDisplay) {
              // Only store the original if not already stored
              if (!nameDisplay.hasAttribute("data-original-text")) {
                nameDisplay.setAttribute(
                  "data-original-text",
                  nameDisplay.innerHTML,
                );
              }

              const displayName = option.colorName || option.title;
              const displayPrice = this.getDisplayPrice(
                optionType,
                option.price,
              );
              const previewText =
                displayPrice > 0
                  ? `${displayName} <span class="inline-price">+$${displayPrice.toFixed(
                      2,
                    )}</span>`
                  : displayName;

              nameDisplay.innerHTML = previewText;
            }
          });

          optionElement.addEventListener("mouseleave", () => {
            const nameDisplay = document.getElementById(hoverNameDisplayId);
            if (nameDisplay) {
              const original = nameDisplay.getAttribute("data-original-text");
              if (original) {
                nameDisplay.innerHTML = original;
                nameDisplay.removeAttribute("data-original-text");
              }
            }
          });
        }

        optionElement.addEventListener("click", () => {
          this.selectOption(optionType, option);
        });

        container.appendChild(optionElement);
      });
    }

    // Normalize node IDs for better matching (convert to lowercase)
    normalizeNodeId(nodeId) {
      return String(nodeId).toLowerCase();
    }

    // Get the effective carabiner node ID based on necklace selection
    getEffectiveCarabinerNodeId() {
      if (!this.currentOptions.carabiner) return null;

      // Check if current necklace has a carabiner override
      if (this.currentOptions.beads) {
        const necklaceName =
          this.currentOptions.beads.colorName ||
          this.currentOptions.beads.title;
        const normalizedNecklaceName = necklaceName.toLowerCase().trim();

        // Get the selected carabiner color
        const carabinerName =
          this.currentOptions.carabiner.colorName ||
          this.currentOptions.carabiner.title;
        const normalizedCarabinerName = carabinerName.toLowerCase().trim();

        // Check for exact match in overrides
        if (this.necklaceCarabinerOverrides[normalizedNecklaceName]) {
          const overrideValue =
            this.necklaceCarabinerOverrides[normalizedNecklaceName];
          // Only apply override if user selected gold carabiner
          if (normalizedCarabinerName.includes("gold")) {
            if (DEBUG_MODE) {
              console.log(
                "[getEffectiveCarabinerNodeId] Applying override for",
                normalizedNecklaceName,
              );
              console.log(
                "[getEffectiveCarabinerNodeId] Override value:",
                overrideValue,
              );
            }
            return overrideValue;
          }
        }

        // Check for partial matches (e.g., "Sonny" matches "sonny")
        for (const [overrideKey, overrideValue] of Object.entries(
          this.necklaceCarabinerOverrides,
        )) {
          if (
            normalizedNecklaceName.includes(overrideKey) ||
            overrideKey.includes(normalizedNecklaceName)
          ) {
            // Only apply override if user selected gold carabiner
            if (normalizedCarabinerName.includes("gold")) {
              return overrideValue;
            }
          }
        }
      }

      // Global carabiner override: If gold carabiner node ID is "Carabiner - Gold", use "Carabiner - Gold001"
      const carabinerNodeId = this.currentOptions.carabiner.nodeId;
      if (carabinerNodeId) {
        const normalizedCarabinerNodeId = this.normalizeNodeId(carabinerNodeId);
        if (normalizedCarabinerNodeId === "carabiner - gold") {
          if (DEBUG_MODE) {
            console.log(
              "[getEffectiveCarabinerNodeId] Applying global gold override: Carabiner - Gold -> Carabiner - Gold001",
            );
          }
          return "Carabiner - Gold001";
        }
      }

      // Return the original carabiner node ID if no override applies
      return this.currentOptions.carabiner.nodeId;
    }

    // Select an option and update the model
    selectOption(optionType, option) {
      this.currentOptions[optionType] = option;

      const optionItems = document.querySelectorAll(
        `.option-item[data-option-type="${optionType}"]`,
      );
      optionItems.forEach((item) => {
        if (item.dataset.optionId === option.id.toString()) {
          item.classList.add("selected");
        } else {
          item.classList.remove("selected");
        }
      });

      // Determine which name display to update (beads vs chain)
      const nameDisplayId = (optionType === "beads" && this.baseType === "metal")
        ? "chain-selected-name"
        : `${optionType}-selected-name`;
      const nameDisplay = document.getElementById(nameDisplayId);
      if (nameDisplay) {
        const displayName = option.colorName || option.title;
        const displayPrice = this.getDisplayPrice(optionType, option.price);
        const finalText =
          displayPrice > 0
            ? `${displayName} <span class="inline-price">+$${displayPrice.toFixed(
                2,
              )}</span>`
            : displayName;

        nameDisplay.innerHTML = finalText;
        nameDisplay.setAttribute("data-original-text", finalText);
      }

      // Update mobile selected name display
      const mobileNameDisplayId = (optionType === "beads" && this.baseType === "metal")
        ? "chain-selected-name-mobile"
        : `${optionType}-selected-name-mobile`;
      const mobileNameDisplay = document.getElementById(mobileNameDisplayId);
      if (mobileNameDisplay) {
        const displayName = option.colorName || option.title;
        const displayPrice = this.getDisplayPrice(optionType, option.price);
        const finalText =
          displayPrice > 0
            ? `${displayName} <span class="inline-price">+$${displayPrice.toFixed(
                2,
              )}</span>`
            : displayName;

        mobileNameDisplay.innerHTML = finalText;
      }
      window.lastCameraPosition = window.isCameraAtFrontView();

      this.updateModel();

      this.updateTotalPrice();

      this.updateShareableUrl();

      // Update selected name in mobile step view if visible
      if (this.currentMobileStep && this.mobileSelectedOptionDetails) {
        const typeForStep = {
          1: "beads",
          2: "carabiner",
          3: "charm",
        }[this.currentMobileStep];

        if (optionType === typeForStep) {
          const nameSpan = this.mobileSelectedOptionDetails.querySelector(
            ".mobile-selected-name",
          );

          let displayText = option.colorName || option.title;
          const displayPrice = this.getDisplayPrice(optionType, option.price);
          if (displayPrice > 0) {
            displayText += ` +$${displayPrice.toFixed(2)}`;
          }

          if (nameSpan) {
            nameSpan.textContent = displayText;
          } else {
            const newSpan = document.createElement("span");
            newSpan.className = "mobile-selected-name";
            newSpan.textContent = displayText;
            this.mobileSelectedOptionDetails.appendChild(newSpan);
          }
        }
      }
    }

    // Update 3D model based on selections
    updateModel(isInitialLoad) {
      if (!window.dripModelViewer) {
        window.addEventListener(
          "model-loaded",
          () => {
            this.updateModel();
          },
          { once: true },
        );
        return;
      }

      // If model isn't loaded yet, just return (selectors can still work)
      if (!window.dripModelViewer.model) {
        return;
      }

      const isBeadsChanging =
        this.currentOptions.beads !== this.previousOptions?.beads;
      const isCarabinerChanging =
        this.currentOptions.carabiner !== this.previousOptions?.carabiner;
      const isCharmChanging =
        this.currentOptions.charm !== this.previousOptions?.charm;
      const cameraHasMoved = !window.isCameraAtFrontView();

      this.previousOptions = { ...this.currentOptions };

      if (this.currentOptions.beads) {
        const nodeId = this.currentOptions.beads.nodeId;

        if (isBeadsChanging) {
          window.resetViewToFront(
            800,
            !cameraHasMoved,
            "necklace",
            false,
            () => {
              window.updateModelNode("necklace", nodeId);
              window.adjustCameraForNecklace(nodeId);
              // Also update carabiner if necklace change affects carabiner display
              if (this.currentOptions.carabiner) {
                const carabinerNodeId = this.getEffectiveCarabinerNodeId();
                window.updateModelNode("carabiner", carabinerNodeId);
              }
              // Also update charm to ensure materials are properly refreshed
              if (
                this.currentOptions.charm &&
                this.currentOptions.charm.id !== "no-charm"
              ) {
                const charmNodeId = this.currentOptions.charm.nodeId;
                window.updateModelNode("charm", charmNodeId);
              }
            },
          );
        } else {
          window.updateModelNode("necklace", nodeId);
          window.adjustCameraForNecklace(nodeId);
        }
      }

      if (this.currentOptions.carabiner) {
        const nodeId = this.getEffectiveCarabinerNodeId();

        if (this.currentOptions.carabiner.id === "no-carabiner") {
          try {
            hideAllNodesOfComponentType("carabiner");
          } catch (error) {
            if (DEBUG_MODE) {
              console.error(
                "[updateModel] Error hiding carabiner nodes:",
                error,
              );
            }
          }
        } else if (isCarabinerChanging) {
          window.resetViewToFront(800, !cameraHasMoved, "necklace", false);
          setTimeout(() => {
            window.updateModelNode("carabiner", nodeId);
            // Re-apply necklace camera offset (e.g. sage zoom) after carabiner reset
            if (this.currentOptions.beads) {
              window.adjustCameraForNecklace(this.currentOptions.beads.nodeId);
            }
          }, 800);
        } else {
          window.updateModelNode("carabiner", nodeId);
        }
      }

      if (this.currentOptions.charm) {
        const nodeId = this.currentOptions.charm.nodeId;

        if (this.currentOptions.charm.id === "no-charm") {
          try {
            hideAllNodesOfComponentType("charm");
          } catch (error) {
            if (DEBUG_MODE) {
              console.error("[updateModel] Error hiding charm nodes:", error);
            }
          }
        } else if (isCharmChanging) {
          window.resetViewToFront(800, !cameraHasMoved, "necklace", false);

          setTimeout(() => {
            window.updateModelNode("charm", nodeId);
            // Re-apply necklace camera offset (e.g. sage zoom) after charm reset
            if (this.currentOptions.beads) {
              window.adjustCameraForNecklace(this.currentOptions.beads.nodeId);
            }
          }, 1500);

          setTimeout(() => {
            if (window.animateSwap) {
              window.animateSwap(nodeId);
            }
          }, 900);
        } else {
          window.updateModelNode("charm", nodeId);
        }
      }
    }

    // Function to format prices with appropriate currency symbol
    formatPriceWithCurrency(price) {
      if (
        typeof Shopify === "undefined" ||
        !Shopify.currency ||
        !Shopify.currency.active
      ) {
        // Default to $ if Shopify currency info is not available
        return "$" + price.toFixed(2);
      }

      const currencyCode = Shopify.currency.active;

      // Define currency symbols and their display format
      const currencyFormats = {
        USD: { symbol: "$", position: "before" },
        CAD: { symbol: "$", position: "before", code: "CAD" },
        EUR: { symbol: "€", position: "before" },
        GBP: { symbol: "£", position: "before" },
        // Add more currencies as needed
      };

      // Get the format for the current currency, or default to $ before
      const format = currencyFormats[currencyCode] || {
        symbol: "$",
        position: "before",
      };

      // Format the number based on currency
      const formattedNumber =
        currencyCode === "EUR"
          ? price.toFixed(2).replace(".", ",")
          : price.toFixed(2);

      // Create the formatted price string
      let formattedPrice = "";

      if (format.position === "before") {
        formattedPrice = format.symbol + formattedNumber;
      } else {
        formattedPrice = formattedNumber + format.symbol;
      }

      // Add currency code for CAD
      if (format.code) {
        formattedPrice += " " + format.code;
      }

      return formattedPrice;
    }

    // Update the updateTotalPrice method
    updateTotalPrice() {
      let totalPrice = this.basePrice;

      Object.keys(this.currentOptions).forEach((optionType) => {
        if (this.currentOptions[optionType]) {
          totalPrice += this.currentOptions[optionType].price || 0;
        }
      });

      const formattedPrice = this.formatPriceWithCurrency(totalPrice);
      this.priceElement.textContent = formattedPrice;
      this.priceElementMobile.textContent = formattedPrice;
    }

    // Get the display price for an option based on its type
    getDisplayPrice(optionType, price) {
      if (optionType === "beads") {
        // For beads, only show price if it's above $130, and show the difference
        if (price > 130) {
          return price - 130;
        }
        return 0; // Don't show price if $130 or below
      } else if (optionType === "carabiner") {
        // For carabiner, only show price if it's above $65, and show the difference
        if (price > 65) {
          return price - 65;
        }
        return 0; // Don't show price if $65 or below
      } else {
        // For charm, show full price if > 0
        return price > 0 ? price : 0;
      }
    }

    // Update UI to reflect current selections
    updateUISelections() {
      Object.keys(this.currentOptions).forEach((optionType) => {
        if (this.currentOptions[optionType]) {
          const selectedOption = this.currentOptions[optionType];
          const optionElements = document.querySelectorAll(
            `.option-item[data-option-type="${optionType}"][data-option-id="${selectedOption.id}"]`,
          );

          // Mark all matching elements as selected (both desktop and mobile)
          optionElements.forEach((el) => el.classList.add("selected"));

          // Update desktop selected name display
          const nameDisplay = document.getElementById(
            `${optionType}-selected-name`,
          );
          if (nameDisplay) {
            const displayName =
              selectedOption.colorName || selectedOption.title;
            const displayPrice = this.getDisplayPrice(
              optionType,
              selectedOption.price,
            );
            if (displayPrice > 0) {
              nameDisplay.innerHTML = `${displayName} <span class="inline-price">+$${displayPrice.toFixed(
                2,
              )}</span>`;
            } else {
              nameDisplay.textContent = displayName;
            }
          }

          // Update mobile selected name display
          const mobileNameDisplay = document.getElementById(
            `${optionType}-selected-name-mobile`,
          );
          if (mobileNameDisplay) {
            const displayName =
              selectedOption.colorName || selectedOption.title;
            const displayPrice = this.getDisplayPrice(
              optionType,
              selectedOption.price,
            );
            if (displayPrice > 0) {
              mobileNameDisplay.innerHTML = `${displayName} <span class="inline-price">+$${displayPrice.toFixed(
                2,
              )}</span>`;
            } else {
              mobileNameDisplay.textContent = displayName;
            }
          }
        }
      });
    }

    // Update URL with current selections for sharing
    updateShareableUrl() {
      const params = new URLSearchParams();

      if (this.currentOptions.beads) {
        params.set("bead", this.currentOptions.beads.id);
      }

      if (this.currentOptions.carabiner) {
        params.set("carabiner", this.currentOptions.carabiner.id);
      }

      if (this.currentOptions.charm) {
        params.set("charm", this.currentOptions.charm.id);
      }

      const newUrl = `${window.location.pathname}?${params.toString()}`;
      window.history.replaceState({}, "", newUrl);
    }

    // Setup mobile step navigation
    setupMobileNavigation() {
      if (DEBUG_MODE) console.log("Setting up mobile navigation...");
      const prevBtn = document.getElementById("mobile-prev-option");
      const nextBtn = document.getElementById("mobile-next-option");

      if (DEBUG_MODE) console.log("Prev button:", prevBtn);
      if (DEBUG_MODE) console.log("Next button:", nextBtn);

      if (!prevBtn || !nextBtn) {
        if (DEBUG_MODE) console.log("Mobile navigation buttons not found");
        return;
      }

      let currentStep = 0;
      const totalSteps = 4;

      const updateMobileStep = (step) => {
        if (DEBUG_MODE) console.log("Updating mobile step to:", step);

        // Hide all option groups
        const allGroups = document.querySelectorAll(".mobile-option-group");
        if (DEBUG_MODE) console.log("Found option groups:", allGroups.length);

        allGroups.forEach((group) => {
          group.classList.remove("active");
        });

        // Show current step (multiple groups may share a step, e.g. beads and chain at step 1)
        const currentGroups = document.querySelectorAll(
          `.mobile-option-group[data-step="${step}"]`,
        );
        if (DEBUG_MODE)
          console.log("Current groups for step", step, ":", currentGroups.length);

        currentGroups.forEach((group) => {
          group.classList.add("active");
        });

        // Update step indicators
        document
          .querySelectorAll(".mobile-step-indicator")
          .forEach((indicator) => {
            const indicatorStep = parseInt(indicator.dataset.step);
            if (indicatorStep === step) {
              indicator.classList.add("active");
              indicator.style.opacity = "1";
            } else {
              indicator.classList.remove("active");
              indicator.style.opacity = "0.3";
            }
          });

        // Update button states
        prevBtn.disabled = step === 0;
        nextBtn.disabled = step === totalSteps - 1;

        // Auto-select first real carabiner when arriving at carabiner step
        if (step === 2 && this.currentOptions.carabiner && this.currentOptions.carabiner.id === "no-carabiner") {
          const firstCarabiner = this.productsData.carabiner.find(
            (opt) => opt.id !== "no-carabiner" && opt.available,
          );
          if (firstCarabiner) {
            this.selectOption("carabiner", firstCarabiner);
          }
        }

        currentStep = step;
      };

      prevBtn.addEventListener("click", (e) => {
        if (DEBUG_MODE)
          console.log("Prev button clicked, current step:", currentStep);
        e.preventDefault();
        if (currentStep > 0) {
          updateMobileStep(currentStep - 1);
        }
      });

      nextBtn.addEventListener("click", (e) => {
        if (DEBUG_MODE)
          console.log("Next button clicked, current step:", currentStep);
        e.preventDefault();
        if (currentStep < totalSteps - 1) {
          updateMobileStep(currentStep + 1);
        }
      });

      // Initialize on first step
      if (DEBUG_MODE) console.log("Initializing to step 0");
      updateMobileStep(0);
    }

    // Show specific mobile step
    showMobileStep(step) {
      // Update step indicator
      this.currentStepElement.textContent = step;
      this.currentMobileStep = step;

      // Clear previous options
      this.mobileOptionsWrapper.innerHTML = "";
      this.mobileSelectedOptionDetails.innerHTML = "";

      let optionType;
      let title;
      let isColorOption;

      switch (step) {
        case 1:
          optionType = "beads";
          title = "Base Color";
          isColorOption = true;
          break;
        case 2:
          optionType = "carabiner";
          title = "Carabiner";
          isColorOption = true;
          break;
        case 3:
          optionType = "charm";
          title = "Charm";
          isColorOption = false;
          break;
      }

      const titleElement = document.createElement("h3");
      titleElement.textContent = title;
      this.mobileSelectedOptionDetails.appendChild(titleElement);

      const selectedOption = this.currentOptions[optionType];
      if (selectedOption) {
        const nameSpan = document.createElement("span");
        nameSpan.className = "mobile-selected-name";

        let displayText = selectedOption.colorName || selectedOption.title;
        if (optionType === "charm" && selectedOption.price > 0) {
          displayText += ` +$${selectedOption.price.toFixed(2)}`;
        }

        nameSpan.textContent = displayText;
        this.mobileSelectedOptionDetails.appendChild(nameSpan);
      }
      const optionsContainer = document.createElement("div");
      optionsContainer.className = "mobile-option-selections";

      this.renderProductOptions(
        optionsContainer,
        this.productsData[optionType],
        optionType,
        isColorOption,
      );

      this.mobileOptionsWrapper.appendChild(optionsContainer);

      this.prevBtn.disabled = step === 1;
      this.nextBtn.disabled = step === this.totalMobileSteps;
    }

    // Add to cart functionality (Bundled)
    addToCart() {
      if (DEBUG_MODE) console.log("addToCart function called");
      if (DEBUG_MODE) console.log("Current options:", this.currentOptions);

      const missingOptions = [];

      if (!this.currentOptions.beads) missingOptions.push("Base");
      if (!this.currentOptions.carabiner) missingOptions.push("Carabiner");

      if (missingOptions.length > 0) {
        alert(
          `Please select the following options: ${missingOptions.join(", ")}`,
        );
        return;
      }
      const bundleId = `drip-${Date.now()}`;

      const items = [];

      // ISMAEEL'S CHANGES
      const selectedOptions = ["beads", "carabiner", "charm"];
      let beadsAdded = false;

      selectedOptions.forEach((type) => {
        const option = this.currentOptions[type];
        if (!option || option.id === "no-charm" || option.id === "no-carabiner")
          return;

        const componentLabel = type === "beads"
          ? (this.baseType === "metal" ? "Chain" : "Beads")
          : type.charAt(0).toUpperCase() + type.slice(1);
        const properties = {
          "Bundle Group": bundleId,
          Component: componentLabel,
          Color: option.colorName || option.title,
        };

        items.push({
          id: option.id,
          properties,
        });

        if (type === "beads") {
          beadsAdded = true;
        }
      });

      if (!beadsAdded) {
        alert(
          "Error: Beads failed to be added to the bundle. Please try again and contact us at info@shopspicydan.com",
        );
        return;
      }

      fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items }),
      })
        .then((response) => {
          if (!response.ok) throw new Error("Network response was not ok");
          return response.json();
        })
        .then((data) => {
          // Update cart UI after successful addition
          if (typeof window.updateCartUI === "function") {
            window.updateCartUI();
            // Note: openCartDropdown is already called within updateCartUI
          }

          this.showConfirmation();
        })
        .catch((error) => {
          console.error("Error adding items to cart:", error);
          alert("There was an error adding this bundle to your cart.");
        });
    }

    showConfirmation() {
      const isHomepage =
        window.location.pathname === "/" ||
        window.location.pathname === "/pages/home";
      const configurator = document.querySelector(".configurator");
      const confirmationContent = document.getElementById(
        "build-a-drip-confirmation-content",
      );

      // Skip confirmation entirely when on homepage
      if (isHomepage) {
        if (DEBUG_MODE) console.log("On homepage: Skipping confirmation modal");
        return;
      }

      if (configurator && confirmationContent) {
        if (DEBUG_MODE) console.log("Showing confirmation content");

        // Not on homepage: Use existing inline confirmation behavior
        configurator.classList.add("hidden");
        confirmationContent.classList.remove("hidden");
        confirmationContent.classList.add("flex");

        // Reset checkboxes for next time
        const finalSaleCheckbox = document.getElementById(
          "final-sale-checkbox",
        );
        const finalSaleCheckboxMobile = document.getElementById(
          "final-sale-checkbox-mobile",
        );
        const addToCartBtn = document.getElementById("add-to-cart");
        const addToCartBtnMobile =
          document.getElementById("add-to-cart-mobile");

        // Check if checkbox requirement is enabled
        const section = document.getElementById("build-a-drip-section");
        const requireCheckbox =
          section && section.dataset.requireFinalSaleCheckbox === "true";

        if (finalSaleCheckbox) finalSaleCheckbox.checked = false;
        if (finalSaleCheckboxMobile) finalSaleCheckboxMobile.checked = false;
        if (addToCartBtn) {
          addToCartBtn.disabled = requireCheckbox; // Only disable if checkbox requirement is enabled
          addToCartBtn.style.cursor = requireCheckbox
            ? "not-allowed"
            : "pointer";
        }
        if (addToCartBtnMobile) {
          addToCartBtnMobile.disabled = requireCheckbox; // Only disable if checkbox requirement is enabled
          addToCartBtnMobile.style.cursor = requireCheckbox
            ? "not-allowed"
            : "pointer";
        }
      } else {
        if (DEBUG_MODE) {
          console.error("Could not find configurator or confirmation content");
          console.log("configurator:", configurator);
          console.log("confirmationContent:", confirmationContent);
        }
      }
    }

    // Handle sharing the current configuration
    shareConfiguration() {
      const shareUrl = window.location.href;

      navigator.clipboard
        .writeText(shareUrl)
        .then(() => {
          alert("Configuration URL copied to clipboard!");
        })
        .catch((err) => {
          console.error("Could not copy URL: ", err);
          alert("The URL for this configuration is: " + shareUrl);
        });
    }

    // Setup all event listeners
    setupEventListeners() {
      if (DEBUG_MODE) console.log("Setting up event listeners...");
      if (DEBUG_MODE) console.log("Add to cart button:", this.addToCartBtn);

      // Check if checkbox requirement is enabled
      const section = document.getElementById("build-a-drip-section");
      const requireCheckbox =
        section && section.dataset.requireFinalSaleCheckbox === "true";

      if (this.addToCartBtn) {
        this.addToCartBtn.addEventListener("click", () => {
          if (DEBUG_MODE) console.log("Add to cart button clicked!");
          this.addToCart();
        });
        this.addToCartBtn.setAttribute("data-listener-added", "true");

        // Enable button if checkbox requirement is disabled
        if (!requireCheckbox) {
          this.addToCartBtn.disabled = false;
          this.addToCartBtn.style.cursor = "pointer";
        }
      } else {
        if (DEBUG_MODE) console.error("Add to cart button not found!");
      }

      if (this.mobileAddToCartBtn) {
        // Enable mobile button if checkbox requirement is disabled
        if (!requireCheckbox) {
          this.mobileAddToCartBtn.disabled = false;
          this.mobileAddToCartBtn.style.cursor = "pointer";
        }
      }

      // Final sale checkbox handler (desktop) - only if checkbox requirement is enabled
      const finalSaleCheckbox = document.getElementById("final-sale-checkbox");
      if (finalSaleCheckbox && this.addToCartBtn && requireCheckbox) {
        finalSaleCheckbox.addEventListener("change", (e) => {
          if (e.target.checked) {
            this.addToCartBtn.disabled = false;
            this.addToCartBtn.style.cursor = "pointer";
          } else {
            this.addToCartBtn.disabled = true;
            this.addToCartBtn.style.cursor = "not-allowed";
          }
        });
      }

      // Final sale checkbox handler (mobile) - only if checkbox requirement is enabled
      const finalSaleCheckboxMobile = document.getElementById(
        "final-sale-checkbox-mobile",
      );
      if (
        finalSaleCheckboxMobile &&
        this.mobileAddToCartBtn &&
        requireCheckbox
      ) {
        finalSaleCheckboxMobile.addEventListener("change", (e) => {
          if (e.target.checked) {
            this.mobileAddToCartBtn.disabled = false;
            this.mobileAddToCartBtn.style.cursor = "pointer";
          } else {
            this.mobileAddToCartBtn.disabled = true;
            this.mobileAddToCartBtn.style.cursor = "not-allowed";
          }
        });
      }

      // Mobile Add to Cart — only binds if screen is smaller than 768px
      if (this.mobileAddToCartBtn && window.innerWidth < 768) {
        this.mobileAddToCartBtn.onclick = () => this.addToCart();
        this.mobileAddToCartBtn.setAttribute("data-listener-added", "true");
      } else if (this.mobileAddToCartBtn) {
        this.mobileAddToCartBtn.onclick = null; // clear any previous listener
      }

      if (this.shareBtn) {
        this.shareBtn.addEventListener("click", () =>
          this.shareConfiguration(),
        );
      }
      if (this.shareBtnMobile) {
        this.shareBtnMobile.addEventListener("click", () =>
          this.shareConfiguration(),
        );
      }

      // Base type toggle buttons
      document.querySelectorAll('.base-type-toggle-btn').forEach((btn) => {
        btn.addEventListener('click', () => {
          this.setBaseType(btn.dataset.baseType);
        });
      });

      if (DEBUG_MODE) console.log("About to call setupMobileNavigation...");
      this.setupMobileNavigation();
      if (DEBUG_MODE) console.log("setupMobileNavigation called");

      if (this.modelViewer) {
        this.modelViewer.addEventListener("mousedown", () => {
          if (window.stopAutoRotate) {
            window.stopAutoRotate();
          }
        });

        this.modelViewer.addEventListener("touchstart", () => {
          if (window.stopAutoRotate) {
            window.stopAutoRotate();
          }
        });
      }
    }

    // Method to programmatically select options by SKU
    selectBySku(beadSku, carabinerSku, charmSku) {
      if (DEBUG_MODE)
        console.log(
          "selectBySku called with:",
          beadSku,
          carabinerSku,
          charmSku,
        );

      // Find and select bead by SKU
      if (beadSku) {
        const beadOption = this.productsData.beads.find(
          (opt) => opt.sku === beadSku,
        );
        if (beadOption) {
          // Auto-detect base type from the bead's node ID
          const newBaseType = (beadOption.nodeId && beadOption.nodeId.startsWith("chain")) ? "metal" : "beads";
          if (newBaseType !== this.baseType) {
            this.baseType = newBaseType;
            this.applyBaseTypeVisibility();
          }
          this.currentOptions.beads = beadOption;
          this.selectOption("beads", beadOption);
          if (DEBUG_MODE) console.log("Selected bead:", beadOption);
        }
      }

      // Find and select carabiner by SKU
      if (carabinerSku) {
        const carabinerOption = this.productsData.carabiner.find(
          (opt) => opt.sku === carabinerSku,
        );
        if (carabinerOption) {
          this.currentOptions.carabiner = carabinerOption;
          this.selectOption("carabiner", carabinerOption);
          if (DEBUG_MODE) console.log("Selected carabiner:", carabinerOption);
        }
      }

      // Find and select charm by SKU
      if (charmSku) {
        const charmOption = this.productsData.charm.find(
          (opt) => opt.sku === charmSku || opt.id === charmSku,
        );
        if (charmOption) {
          this.currentOptions.charm = charmOption;
          this.selectOption("charm", charmOption);
          if (DEBUG_MODE) console.log("Selected charm:", charmOption);
        }
      }

      // Update the display
      this.updatePrice();
      this.renderOptions();
    }
  }

  window.normalizeNodeName = function (name) {
    return name.replace(/\s+/g, "_-_-").replace(/[^a-zA-Z0-9_-]/g, "");
  };

  // Necklace-specific camera overrides (zoom closer + move up for smaller necklaces)
  const necklaceCameraOverrides = {
    // sage: { zoomFactor: 0.45, yOffset: -8.0 },
    // Add more overrides as needed:
    // "candy": { zoomDelta: -1.0, yOffset: 0.5 },
  };

  // Smoothly adjust camera zoom and y-offset for specific necklaces, or reset to default
  window.adjustCameraForNecklace = function (nodeId) {
    if (
      !window.dripModelViewer ||
      !window.dripModelViewer.camera ||
      !window.initialCameraState
    )
      return;

    const necklaceName = (nodeId || "")
      .replace(/^necklace\s*-?\s*/i, "")
      .trim()
      .toLowerCase();

    const override = necklaceCameraOverrides[necklaceName] || null;
    const cam = window.dripModelViewer.camera;
    const controls = window.dripModelViewer.controls;
    const front = window.initialCameraState;

    // Target camera state: default front view + override offsets
    // zoomFactor < 1 moves camera closer (e.g. 0.4 = 40% of default distance = 2.5x zoom)
    const targetZ = front.position.z * (override ? override.zoomFactor : 1);
    const targetY = front.position.y + (override ? override.yOffset : 0);
    const targetTY = front.target.y + (override ? override.yOffset * 0.5 : 0);

    const startZ = cam.position.z;
    const startY = cam.position.y;
    const startTY = controls.target.y;

    const duration = 600;
    const startTime = performance.now();

    function animateCamera(now) {
      const elapsed = now - startTime;
      const t = Math.min(elapsed / duration, 1);
      const ease = t * (2 - t); // ease-out

      cam.position.z = startZ + (targetZ - startZ) * ease;
      cam.position.y = startY + (targetY - startY) * ease;
      controls.target.y = startTY + (targetTY - startTY) * ease;
      controls.update();

      if (t < 1) requestAnimationFrame(animateCamera);
    }

    requestAnimationFrame(animateCamera);
  };

  // Save initial camera position
  window.saveCameraState = function () {
    if (window.dripModelViewer && window.dripModelViewer.camera) {
      window.initialCameraState = {
        position: {
          x: window.dripModelViewer.camera.position.x,
          y: window.dripModelViewer.camera.position.y,
          z: window.dripModelViewer.camera.position.z,
        },
        target: window.dripModelViewer.controls
          ? {
              x: window.dripModelViewer.controls.target.x,
              y: window.dripModelViewer.controls.target.y,
              z: window.dripModelViewer.controls.target.z,
            }
          : null,
      };
    }
  };

  // Reset view to show the necklace from the front with rotation animation
  window.resetViewToFront = function (
    duration = 1000,
    forceRotate = false,
    componentType = null,
    forceAlways = false,
    onComplete = null, // optional callback when animation (or immediate skip) finishes
  ) {
    if (
      !window.dripModelViewer ||
      !window.dripModelViewer.controls ||
      !window.initialCameraState
    )
      return;

    const frontView = window.initialCameraState;

    const currentPosition = {
      x: window.dripModelViewer.camera.position.x,
      y: window.dripModelViewer.camera.position.y,
      z: window.dripModelViewer.camera.position.z,
    };

    const currentTarget = {
      x: window.dripModelViewer.controls.target.x,
      y: window.dripModelViewer.controls.target.y,
      z: window.dripModelViewer.controls.target.z,
    };

    const vectorX = currentPosition.x - currentTarget.x;
    const vectorZ = currentPosition.z - currentTarget.z;

    const currentRadius = Math.sqrt(vectorX * vectorX + vectorZ * vectorZ);

    const currentAngle = Math.atan2(vectorX, vectorZ);

    const targetVectorX = frontView.position.x - frontView.target.x;
    const targetVectorZ = frontView.position.z - frontView.target.z;
    const targetRadius = Math.sqrt(
      targetVectorX * targetVectorX + targetVectorZ * targetVectorZ,
    );
    const targetAngle = Math.atan2(targetVectorX, targetVectorZ);

    const angleDifference =
      Math.abs(targetAngle - currentAngle) % (2 * Math.PI);
    const radiusDifference = Math.abs(targetRadius - currentRadius);
    const positionYDifference = Math.abs(
      frontView.position.y - currentPosition.y,
    );
    const targetPointDifference =
      Math.abs(frontView.target.x - currentTarget.x) +
      Math.abs(frontView.target.y - currentTarget.y) +
      Math.abs(frontView.target.z - currentTarget.z);

    const isAtFront =
      angleDifference < 0.1 &&
      radiusDifference < 0.5 &&
      positionYDifference < 0.5 &&
      targetPointDifference < 0.5;

    // Check if we need to reset to front or do a 360-degree rotation
    if (
      !forceAlways &&
      isAtFront &&
      (forceRotate || componentType === "necklace")
    ) {
      // Skip animation when already at front unless forced; still invoke callback
      if (typeof onComplete === "function") onComplete();
      return;
    }

    let angleDistance = targetAngle - currentAngle;

    if (angleDistance > Math.PI) angleDistance -= 2 * Math.PI;
    if (angleDistance < -Math.PI) angleDistance += 2 * Math.PI;

    const startTime = Date.now();

    function animate() {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);

      const easeProgress = 0.5 - 0.5 * Math.cos(progress * Math.PI);

      const newRadius =
        currentRadius + (targetRadius - currentRadius) * easeProgress;

      const newAngle = currentAngle + angleDistance * easeProgress;

      const newX = currentTarget.x + Math.sin(newAngle) * newRadius;
      const newZ = currentTarget.z + Math.cos(newAngle) * newRadius;

      const newY =
        currentPosition.y +
        (frontView.position.y - currentPosition.y) * easeProgress;

      window.dripModelViewer.camera.position.set(newX, newY, newZ);

      window.dripModelViewer.controls.target.x =
        currentTarget.x + (frontView.target.x - currentTarget.x) * easeProgress;
      window.dripModelViewer.controls.target.y =
        currentTarget.y + (frontView.target.y - currentTarget.y) * easeProgress;
      window.dripModelViewer.controls.target.z =
        currentTarget.z + (frontView.target.z - currentTarget.z) * easeProgress;

      window.dripModelViewer.controls.update();

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        if (componentType === "necklace") {
          // performRotationAnimation(duration);
        }
        if (typeof onComplete === "function") onComplete();
      }
    }

    animate();
  };

  // Perform a 360-degree rotation animation
  window.performRotationAnimation = function (duration = 1500) {
    if (
      !window.dripModelViewer ||
      !window.dripModelViewer.controls ||
      !window.initialCameraState
    )
      return;

    const currentPosition = {
      x: window.dripModelViewer.camera.position.x,
      y: window.dripModelViewer.camera.position.y,
      z: window.dripModelViewer.camera.position.z,
    };

    const currentTarget = {
      x: window.dripModelViewer.controls.target.x,
      y: window.dripModelViewer.controls.target.y,
      z: window.dripModelViewer.controls.target.z,
    };

    const vectorX = currentPosition.x - currentTarget.x;
    const vectorZ = currentPosition.z - currentTarget.z;

    const currentRadius = Math.sqrt(vectorX * vectorX + vectorZ * vectorZ);
    const startAngle = Math.atan2(vectorX, vectorZ);

    const startTime = Date.now();

    function rotateCamera() {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Use easing function for smoother rotation
      const easeProgress =
        progress < 0.5
          ? 4 * progress * progress * progress
          : 1 - Math.pow(-2 * progress + 2, 3) / 2;

      // Rotate 360 degrees (2π radians) clockwise
      const newAngle = startAngle + 2 * Math.PI * easeProgress;

      const newX = currentTarget.x + Math.sin(newAngle) * currentRadius;
      const newZ = currentTarget.z + Math.cos(newAngle) * currentRadius;

      window.dripModelViewer.camera.position.set(newX, currentPosition.y, newZ);
      window.dripModelViewer.controls.update();

      if (progress < 1) {
        requestAnimationFrame(rotateCamera);
      }
    }

    rotateCamera();
  };

  // Enhanced function to animate carabiner swap
  window.animateSwap = function (nodeId) {
    if (!window.dripModelViewer) return;

    if (!nodeId) return;

    const formattedNodeId = formatNodeName(nodeId);
    let carabiner = null;

    if (window.dripModelViewer.model.getObjectByName) {
      carabiner = window.dripModelViewer.model.getObjectByName(formattedNodeId);
    }

    const originalScale = {
      x: carabiner.scale.x,
      y: carabiner.scale.y,
      z: carabiner.scale.z,
    };
    const originalPosition = {
      x: carabiner.position.x,
      y: carabiner.position.y,
      z: carabiner.position.z,
    };

    const duration = 800;
    const startTime = Date.now();

    function animate() {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);

      if (progress <= 0.5) {
        const scaleProgress = 1 - progress * 2; // 1 to 0
        carabiner.scale.set(
          originalScale.x * scaleProgress,
          originalScale.y * scaleProgress,
          originalScale.z * scaleProgress,
        );
      } else {
        const scaleProgress = (progress - 0.5) * 2; // 0 to 1
        carabiner.scale.set(
          originalScale.x * scaleProgress,
          originalScale.y * scaleProgress,
          originalScale.z * scaleProgress,
        );
      }

      window.dripModelViewer.controls.update();

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        carabiner.scale.set(originalScale.x, originalScale.y, originalScale.z);
        carabiner.position.set(
          originalPosition.x,
          originalPosition.y,
          originalPosition.z,
        );
        window.dripModelViewer.controls.update();
      }
    }

    animate();
  };

  // Check if camera has been moved by user
  window.isCameraAtFrontView = function () {
    if (
      !window.dripModelViewer ||
      !window.dripModelViewer.controls ||
      !window.initialCameraState
    )
      return true;

    const currentPosition = window.dripModelViewer.camera.position;
    const frontPosition = window.initialCameraState.position;
    const currentTarget = window.dripModelViewer.controls.target;
    const frontTarget = window.initialCameraState.target;

    const positionDifference =
      Math.abs(frontPosition.x - currentPosition.x) +
      Math.abs(frontPosition.y - currentPosition.y) +
      Math.abs(frontPosition.z - currentPosition.z);

    const targetDifference =
      Math.abs(frontTarget.x - currentTarget.x) +
      Math.abs(frontTarget.y - currentTarget.y) +
      Math.abs(frontTarget.z - currentTarget.z);

    // Consider the camera at front if the position difference is small
    return positionDifference < 0.5 && targetDifference < 0.5;
  };

  window.updateModelNode = function (componentType, nodeId) {
    if (!window.dripModelViewer) return;

    const formattedNodeId = formatNodeName(nodeId);
    if (DEBUG_MODE)
      console.log(
        `[updateModelNode] Looking for ${componentType} node: "${nodeId}" -> formatted: "${formattedNodeId}"`,
      );
    let targetNode = null;

    // Special case: If nodeId is already in underscore format (like NECKLACE_BEAN001), try it directly first
    if (nodeId.includes("_") && !nodeId.includes(" ")) {
      targetNode = window.dripModelViewer.model.getObjectByName(nodeId);
      if (DEBUG_MODE) {
        console.log(
          `[updateModelNode] Direct underscore format "${nodeId}": ${
            targetNode ? "FOUND" : "not found"
          }`,
        );
      }
      if (targetNode) {
        // Found it directly, skip to visibility logic
        hideAllNodesOfComponentType(componentType);
        setNodeAndChildrenVisibility(targetNode, true);
        if (targetNode.material) {
          fixMaterialOpacity(targetNode.material, targetNode.name, targetNode);
        }
        targetNode.traverse((child) => {
          if (child.material) {
            fixMaterialOpacity(child.material, child.name, child);
          }
        });
        return;
      }
    }

    // Try to find node with space format first (original GLB format: "Carabiner - Gold")
    const spaceFormatNodeId = nodeId
      .split(" ")
      .map((part, index) => {
        if (part === "-") return "-";
        if (index === 0 && componentType === "necklace")
          return part.toUpperCase();
        return part.charAt(0).toUpperCase() + part.slice(1);
      })
      .join(" ");

    // Also create an all-uppercase version for GLTF files that use all caps
    const upperCaseFormatNodeId = nodeId
      .split(" ")
      .map((part) => {
        if (part === "-") return "-";
        return part.toUpperCase();
      })
      .join(" ");

    // Create uppercase with underscores (e.g., "NECKLACE_-_STELLA")
    const upperCaseUnderscoreNodeId = nodeId
      .split(" ")
      .map((part) => {
        if (part === "-") return "-";
        return part.toUpperCase();
      })
      .join("_");

    if (window.dripModelViewer.model.getObjectByName) {
      // Try space format first (most common in GLB files)
      targetNode =
        window.dripModelViewer.model.getObjectByName(spaceFormatNodeId);
      if (DEBUG_MODE)
        console.log(
          `[updateModelNode] Space format "${spaceFormatNodeId}": ${
            targetNode ? "FOUND" : "not found"
          }`,
        );

      // If not found, try all uppercase format with spaces (e.g., "NECKLACE - STELLA")
      if (!targetNode) {
        targetNode = window.dripModelViewer.model.getObjectByName(
          upperCaseFormatNodeId,
        );
        if (DEBUG_MODE)
          console.log(
            `[updateModelNode] Uppercase format "${upperCaseFormatNodeId}": ${
              targetNode ? "FOUND" : "not found"
            }`,
          );
      }

      // If not found, try all uppercase format with underscores (e.g., "NECKLACE_-_STELLA")
      if (!targetNode) {
        targetNode = window.dripModelViewer.model.getObjectByName(
          upperCaseUnderscoreNodeId,
        );
        if (DEBUG_MODE)
          console.log(
            `[updateModelNode] Uppercase underscore format "${upperCaseUnderscoreNodeId}": ${
              targetNode ? "FOUND" : "not found"
            }`,
          );
      }

      // If not found with spaces, try with underscores in title case (legacy format)
      if (!targetNode) {
        targetNode =
          window.dripModelViewer.model.getObjectByName(formattedNodeId);
        if (DEBUG_MODE)
          console.log(
            `[updateModelNode] Underscore format "${formattedNodeId}": ${
              targetNode ? "FOUND" : "not found"
            }`,
          );
      }

      // If still not found, try case-insensitive search
      if (!targetNode) {
        const searchLower = spaceFormatNodeId.toLowerCase();
        window.dripModelViewer.model.traverse((node) => {
          if (node.name && node.name.toLowerCase() === searchLower) {
            targetNode = node;
            if (DEBUG_MODE)
              console.log(
                `[updateModelNode] Case-insensitive match found: "${node.name}"`,
              );
          }
        });
      }
    } else {
      const matchingNodes = [];
      findNodesByName(
        window.dripModelViewer.model,
        formattedNodeId,
        matchingNodes,
      );
      if (matchingNodes.length > 0) {
        targetNode = matchingNodes[0];
      }
    }

    // Only look for material nodes for necklace/beads components
    // Carabiners and charms don't have separate material nodes
    const materialNodes =
      componentType === "necklace" || componentType === "beads"
        ? findRelatedMaterialNodes(
            formattedNodeId,
            spaceFormatNodeId,
            upperCaseFormatNodeId,
            upperCaseUnderscoreNodeId,
          )
        : [];

    if (targetNode) {
      hideAllNodesOfComponentType(componentType);
      setNodeAndChildrenVisibility(targetNode, true);

      if (targetNode.material) {
        fixMaterialOpacity(targetNode.material, targetNode.name, targetNode);
      }

      targetNode.traverse((child) => {
        if (child.material) {
          fixMaterialOpacity(child.material, child.name, child);
        }
      });

      // Special case: Sir necklace needs both "BEADS_-_SIR" and "BEADS_-_SIR001" visible
      if (DEBUG_MODE) {
        console.log("[updateModelNode] targetNode.name:", targetNode.name);
      }
      if (
        targetNode.name &&
        (targetNode.name === "BEADS_-_SIR" || targetNode.name === "BEADS - SIR")
      ) {
        if (DEBUG_MODE) {
          console.log(
            "[updateModelNode] Sir necklace detected, handling special case",
          );
        }
        const sirVariant =
          window.dripModelViewer.model.getObjectByName("BEADS_-_SIR001");
        if (sirVariant) {
          if (DEBUG_MODE) {
            console.log(
              "[updateModelNode] Also showing BEADS_-_SIR001 for sir necklace",
            );
            console.log(
              "[updateModelNode] BEADS_-_SIR parent:",
              targetNode.parent?.name,
            );
            console.log(
              "[updateModelNode] BEADS_-_SIR001 parent:",
              sirVariant.parent?.name,
            );
            console.log(
              "[updateModelNode] Are they siblings?",
              targetNode.parent === sirVariant.parent,
            );
          }
          setNodeAndChildrenVisibility(sirVariant, true);
          if (sirVariant.material) {
            fixMaterialOpacity(
              sirVariant.material,
              sirVariant.name,
              sirVariant,
            );
          }
          sirVariant.traverse((child) => {
            if (child.material) {
              fixMaterialOpacity(child.material, child.name, child);
            }
          });

          // Make sure the parent chain is visible too
          let currentNode = sirVariant.parent;
          while (currentNode) {
            currentNode.visible = true;
            currentNode = currentNode.parent;
          }

          // Also show numbered variants of BEADS_-_SIR001 (e.g., BEADS_-_SIR001_1, _2, _3)
          if (sirVariant.parent) {
            sirVariant.parent.children.forEach((sibling) => {
              if (sibling.name && sibling.name.startsWith("BEADS_-_SIR001_")) {
                setNodeAndChildrenVisibility(sibling, true);
                if (sibling.material) {
                  fixMaterialOpacity(sibling.material, sibling.name, sibling);
                }
                sibling.traverse((child) => {
                  if (child.material) {
                    fixMaterialOpacity(child.material, child.name, child);
                  }
                });
                if (DEBUG_MODE) {
                  console.log(
                    "[updateModelNode] Also showing variant:",
                    sibling.name,
                  );
                }
              }
            });
          }

          // Check children of BEADS_-_SIR001 too
          if (sirVariant.children && sirVariant.children.length > 0) {
            sirVariant.children.forEach((child) => {
              if (child.name && child.name.startsWith("BEADS_-_SIR001_")) {
                setNodeAndChildrenVisibility(child, true);
                if (child.material) {
                  fixMaterialOpacity(child.material, child.name, child);
                }
                child.traverse((grandchild) => {
                  if (grandchild.material) {
                    fixMaterialOpacity(
                      grandchild.material,
                      grandchild.name,
                      grandchild,
                    );
                  }
                });
                if (DEBUG_MODE) {
                  console.log(
                    "[updateModelNode] Also showing child variant:",
                    child.name,
                  );
                }
              }
            });
          }
        } else if (DEBUG_MODE) {
          console.warn(
            "[updateModelNode] BEADS_-_SIR001 node not found for sir necklace",
          );
        }
      }

      materialNodes.forEach((node) => {
        setNodeAndChildrenVisibility(node, true);

        if (node.material) {
          fixMaterialOpacity(node.material, node.name, node);
        }

        node.traverse((child) => {
          if (child.material) {
            fixMaterialOpacity(child.material, child.name, child);
          }
        });
      });

      if (targetNode.parent) {
        // Build a list of nodes to keep visible:
        // - targetNode itself
        // - materialNodes (spacers, etc.)
        // - numbered variants like "NECKLACE_-_Garth_1", "NECKLACE_-_Garth_2", etc.
        const nodesToKeepVisible = new Set([targetNode, ...materialNodes]);

        // Special case: Add BEADS_-_SIR001 to keep visible for sir necklace
        if (
          targetNode.name &&
          (targetNode.name === "BEADS_-_SIR" ||
            targetNode.name === "BEADS - SIR")
        ) {
          const sirVariant =
            window.dripModelViewer.model.getObjectByName("BEADS_-_SIR001");
          if (sirVariant) {
            nodesToKeepVisible.add(sirVariant);
            if (DEBUG_MODE) {
              console.log(
                "[updateModelNode] Added BEADS_-_SIR001 to nodesToKeepVisible",
              );
            }

            // Also add numbered variants of BEADS_-_SIR001 (e.g., BEADS_-_SIR001_1, _2, _3)
            if (sirVariant.parent) {
              sirVariant.parent.children.forEach((sibling) => {
                if (
                  sibling.name &&
                  sibling.name.startsWith("BEADS_-_SIR001_")
                ) {
                  nodesToKeepVisible.add(sibling);
                  if (DEBUG_MODE) {
                    console.log(
                      "[updateModelNode] Added variant to nodesToKeepVisible:",
                      sibling.name,
                    );
                  }
                }
              });
            }

            // Check children of BEADS_-_SIR001 too
            if (sirVariant.children && sirVariant.children.length > 0) {
              sirVariant.children.forEach((child) => {
                if (child.name && child.name.startsWith("BEADS_-_SIR001_")) {
                  nodesToKeepVisible.add(child);
                  if (DEBUG_MODE) {
                    console.log(
                      "[updateModelNode] Added child variant to nodesToKeepVisible:",
                      child.name,
                    );
                  }
                }
              });
            }

            if (DEBUG_MODE) {
              console.log(
                "[updateModelNode] nodesToKeepVisible now has size:",
                nodesToKeepVisible.size,
              );
            }
          }
        }

        // CRITICAL: Check for numbered variants as BOTH siblings AND children
        const targetBaseName = targetNode.name;
        const numberPattern = new RegExp(
          `^${targetBaseName.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}_\\d+$`,
        );
        // Additional pattern for 3-digit numbered variants (e.g., _001, _002)
        const threeDigitPattern = new RegExp(
          `^${targetBaseName.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\d{3}$`,
        );
        // Additional pattern for dot-numbered variants (e.g., .001, .002)
        const dotNumberPattern = new RegExp(
          `^${targetBaseName.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\.\\d+$`,
        );

        if (DEBUG_MODE && targetBaseName.includes("SAPPHIRE")) {
          console.log("[updateModelNode] Sapphire base name:", targetBaseName);
          console.log(
            "[updateModelNode] Looking for numbered variants with patterns:",
          );
          console.log("  - Underscore pattern:", numberPattern.source);
          console.log("  - Three digit pattern:", threeDigitPattern.source);
          console.log("  - Dot pattern:", dotNumberPattern.source);
          console.log(
            "[updateModelNode] Siblings:",
            targetNode.parent?.children?.map((c) => c.name),
          );
        }

        // Check siblings
        targetNode.parent.children.forEach((sibling) => {
          if (
            sibling.name &&
            (numberPattern.test(sibling.name) ||
              threeDigitPattern.test(sibling.name) ||
              dotNumberPattern.test(sibling.name))
          ) {
            nodesToKeepVisible.add(sibling);
            setNodeAndChildrenVisibility(sibling, true);
            if (DEBUG_MODE) {
              console.log(
                "[updateModelNode] Found and showing numbered sibling:",
                sibling.name,
              );
              console.log(
                "[updateModelNode] Sibling has material?",
                sibling.material ? "yes" : "no",
              );
              if (sibling.material) {
                console.log(
                  "[updateModelNode] Material opacity:",
                  sibling.material.opacity,
                );
              }
            }
          }
        });

        // Check children (the numbered nodes might be children, not siblings!)
        if (targetNode.children && targetNode.children.length > 0) {
          targetNode.children.forEach((child) => {
            if (
              child.name &&
              (numberPattern.test(child.name) ||
                threeDigitPattern.test(child.name) ||
                dotNumberPattern.test(child.name))
            ) {
              nodesToKeepVisible.add(child);
              // Already set visible by setNodeAndChildrenVisibility above, but ensure it
              setNodeAndChildrenVisibility(child, true);
            }
          });
        }

        targetNode.parent.children.forEach((sibling) => {
          if (!nodesToKeepVisible.has(sibling)) {
            // Check if this sibling is a collection/container that holds our target
            const isCollection =
              sibling.name && sibling.name.includes("COLLECTION");
            if (isCollection) return;

            // Only hide siblings that are the same component type.
            // New nodes at the scene root have ALL other nodes as siblings,
            // so hiding indiscriminately would hide necklaces/charms/etc.
            const sibName = (sibling.name || "").toLowerCase();
            const ctLower = componentType.toLowerCase();
            const isSameType =
              sibName.startsWith(ctLower + "_-_") ||
              sibName.startsWith(ctLower + " - ") ||
              sibName.startsWith(ctLower + ".") ||
              sibName === ctLower ||
              (ctLower === "necklace" &&
                (sibName.startsWith("beads_-_") ||
                  sibName.startsWith("beads - ")));

            if (isSameType) {
              if (DEBUG_MODE && sibling.name === "BEADS_-_SIR001") {
                console.log(
                  "[updateModelNode] WARNING: About to hide BEADS_-_SIR001 (not in nodesToKeepVisible)",
                );
                console.log(
                  "[updateModelNode] nodesToKeepVisible size:",
                  nodesToKeepVisible.size,
                );
              }
              setNodeAndChildrenVisibility(sibling, false);
            }
          }
        });

        let currentNode = targetNode.parent;
        while (currentNode) {
          currentNode.visible = true;
          currentNode = currentNode.parent;
        }
      }

      handleInnerBeadsMaterial(componentType, nodeId);
    } else {
      if (DEBUG_MODE)
        console.warn(`Node '${formattedNodeId}' not found in model`);

      // Additional debug info - show ALL relevant nodes
      if (DEBUG_MODE) {
        console.warn("[BuildADrip] Node not found. Debugging:");
        console.warn(
          `  Looking for: "${spaceFormatNodeId}" or "${formattedNodeId}"`,
        );
        console.warn(`  Component: ${componentType}`);

        if (window.dripModelViewer && window.dripModelViewer.model) {
          const allNodes = [];
          const relevantNodes = [];

          window.dripModelViewer.model.traverse((n) => {
            if (n.name) {
              allNodes.push(n.name);
              const lower = n.name.toLowerCase();
              if (
                lower.includes(componentType) ||
                lower.includes("spacer") ||
                lower.includes("carabiner") ||
                lower.includes("charm")
              ) {
                relevantNodes.push(n.name);
              }
            }
          });

          console.warn(
            `[BuildADrip] ${relevantNodes.length} relevant nodes:`,
            relevantNodes.slice(0, 20),
          );
          console.warn(`[BuildADrip] Total named nodes: ${allNodes.length}`);

          // Look for similar names
          const searchLower = spaceFormatNodeId.toLowerCase();
          const similar = allNodes.filter(
            (name) =>
              name.toLowerCase().includes(searchLower.split(" ")[0]) ||
              name.toLowerCase() === searchLower,
          );
          if (similar.length > 0) {
            console.warn(`[BuildADrip] Similar names:`, similar);
          }
        }
      }
    }
  };

  function setNodeAndChildrenVisibility(node, isVisible) {
    if (!node) return;

    node.visible = isVisible;

    if (isVisible && node.material) {
      fixMaterialOpacity(node.material, node.name, node);
      if (DEBUG_MODE && node.name && node.name.includes("SAPPHIRE")) {
        console.log(
          "[setNodeAndChildrenVisibility] Setting visibility for sapphire node:",
          node.name,
        );
        if (Array.isArray(node.material)) {
          console.log(
            "[setNodeAndChildrenVisibility] Multiple materials:",
            node.material.map((m) => m.name),
          );
        } else {
          console.log(
            "[setNodeAndChildrenVisibility] Material name:",
            node.material.name,
          );
        }
      }
    }

    if (node.children) {
      node.traverse((child) => {
        child.visible = isVisible;
        if (isVisible && child.material) {
          fixMaterialOpacity(child.material, child.name, child);
          if (
            DEBUG_MODE &&
            (node.name?.includes("SAPPHIRE") ||
              child.name?.includes("SAPPHIRE"))
          ) {
            console.log(
              "[setNodeAndChildrenVisibility] Child node:",
              child.name || "unnamed",
            );
            if (Array.isArray(child.material)) {
              console.log(
                "[setNodeAndChildrenVisibility] Child has multiple materials:",
                child.material.map((m) => m.name),
              );
            } else {
              console.log(
                "[setNodeAndChildrenVisibility] Child material name:",
                child.material.name,
              );
            }
          }
        }
      });
    }
  }

  function fixMaterialOpacity(material, nodeName = "", node = null) {
    if (Array.isArray(material)) {
      material.forEach((mat) => fixMaterialOpacity(mat, nodeName, node));
      return;
    }

    // Don't modify carabiner or charm materials - they should maintain their own properties
    const lowerName = nodeName.toLowerCase();
    if (lowerName.includes("carabiner") || lowerName.includes("charm")) {
      material.needsUpdate = true;
      return;
    }

    // Don't fix opacity for spacer materials - they should maintain their transparency
    if (lowerName.includes("spacer")) {
      material.needsUpdate = true;
      return;
    }

    // Handle transmission materials (KHR_materials_transmission from GLTF)
    // MeshPhysicalMaterial with transmission retains shader state even after zeroing
    // the value, causing texture alpha to leak through. Replace with a clean
    // MeshStandardMaterial so the shader has no transmission code paths at all.
    // Skip nodes that have their own special handling (sapphire, apple).
    if (
      material.transmission &&
      material.transmission > 0 &&
      !lowerName.includes("sapphire") &&
      !lowerName.includes("apple")
    ) {
      if (node) {
        const oldMat = material;
        // Build an opaque copy of the texture by forcing all alpha to 255.
        // This prevents the RGBA texture alpha from leaking through.
        let opaqueMap = null;
        if (oldMat.map && oldMat.map.image) {
          try {
            const img = oldMat.map.image;
            const w = img.width || img.naturalWidth;
            const h = img.height || img.naturalHeight;
            if (w && h) {
              const canvas = document.createElement("canvas");
              canvas.width = w;
              canvas.height = h;
              const ctx = canvas.getContext("2d");
              ctx.drawImage(img, 0, 0, w, h);
              const imageData = ctx.getImageData(0, 0, w, h);
              const px = imageData.data;
              for (let i = 3; i < px.length; i += 4) {
                px[i] = 255;
              }
              ctx.putImageData(imageData, 0, 0);
              opaqueMap = new THREE.CanvasTexture(canvas);
              opaqueMap.encoding = oldMat.map.encoding;
              opaqueMap.wrapS = oldMat.map.wrapS;
              opaqueMap.wrapT = oldMat.map.wrapT;
              opaqueMap.flipY = oldMat.map.flipY;
              opaqueMap.needsUpdate = true;
            }
          } catch (e) {
            if (DEBUG_MODE)
              console.warn(
                "[fixMaterialOpacity] Could not strip texture alpha:",
                e,
              );
            opaqueMap = oldMat.map; // fallback to original
          }
        }

        // For materials without a texture, try to extract the intended color
        // from the material name (e.g. "CANDY BLUE - E3F9FF.001" → #E3F9FF).
        // Blender exports transmission colors only in shader nodes which don't
        // map to GLTF PBR, so the hex code in the name is the real bead color.
        let matColor = oldMat.color
          ? oldMat.color.clone()
          : new THREE.Color(1, 1, 1);
        if (!oldMat.map && oldMat.name) {
          // Skip hex extraction for spacer materials — their GLTF baseColorFactor
          // is the correct deep red; the hex in the name (e.g. FF6E72) is lighter/pink.
          const nameForHexCheck = (oldMat.name || "").toLowerCase();
          if (!nameForHexCheck.includes("spacer")) {
            const hexMatch = oldMat.name.match(/\b([0-9A-Fa-f]{6})\b/);
            if (hexMatch) {
              matColor = new THREE.Color("#" + hexMatch[1]);
              if (DEBUG_MODE) {
                console.log(
                  "[fixMaterialOpacity] Extracted color from material name:",
                  oldMat.name,
                  "→ #" + hexMatch[1],
                );
              }
            }
          }
        }

        const newMat = new THREE.MeshStandardMaterial({
          color: matColor,
          map: opaqueMap,
          metalness: oldMat.metalness ?? 0,
          roughness: oldMat.roughness ?? 0.5,
          normalMap: oldMat.normalMap || null,
          aoMap: oldMat.aoMap || null,
          emissive: oldMat.emissive
            ? oldMat.emissive.clone()
            : new THREE.Color(0, 0, 0),
          emissiveMap: oldMat.emissiveMap || null,
          envMap: oldMat.envMap || null,
          envMapIntensity: oldMat.envMapIntensity ?? 1,
          side: THREE.DoubleSide,
          depthWrite: true,
          alphaTest: 0,
          name: oldMat.name,
        });

        // Per-material translucency overrides
        const matNameLower = (oldMat.name || "").toLowerCase();
        if (matNameLower.includes("spacer")) {
          // Spacer beads (e.g. "RUBY SPACER - GLASS FF6E72") should be a solid deep red.
          // Fully opaque so glowing sage stones behind don't bleed through.
          newMat.color = new THREE.Color("#ff0015");
          newMat.opacity = 1.0;
          newMat.transparent = false;
          newMat.depthWrite = true;
          newMat.roughness = 0.05;
          newMat.metalness = 0.0;
          newMat.envMapIntensity = 0.0;
        } else if (matNameLower.includes("clear quartz")) {
          // Pure white translucent glass — no texture, just clean glass
          newMat.map = null;
          newMat.opacity = 0.4;
          newMat.transparent = true;
          newMat.depthWrite = false;
          newMat.roughness = 0.0;
          newMat.metalness = 0.15;
          newMat.envMapIntensity = 5.0;
          newMat.color = new THREE.Color(0.97, 0.98, 1.0);
          newMat.emissive = new THREE.Color(0.15, 0.15, 0.18);
        } else if (
          matNameLower.includes("candy") &&
          !matNameLower.includes("bead")
        ) {
          newMat.opacity = 0.65;
          newMat.transparent = true;
          newMat.depthWrite = false;
          // Boost candy color vibrancy
          const hsl = {};
          newMat.color.getHSL(hsl);
          newMat.color.setHSL(hsl.h, Math.min(hsl.s * 2.0, 1.0), hsl.l * 0.85);
        } else if (
          (matNameLower.includes("scarlet") ||
            matNameLower.includes("glass")) &&
          !matNameLower.includes("bead")
        ) {
          newMat.opacity = 0.65;
          newMat.transparent = true;
          newMat.depthWrite = false;
        } else {
          newMat.opacity = 1.0;
          newMat.transparent = false;
        }

        node.material = newMat;
        if (DEBUG_MODE) {
          console.log(
            "[fixMaterialOpacity] Replaced transmission material with MeshStandardMaterial:",
            nodeName,
            {
              materialName: oldMat.name,
              hadMap: !!oldMat.map,
              hadAlphaMap: !!oldMat.alphaMap,
              oldTransmission: oldMat.transmission,
              oldOpacity: oldMat.opacity,
            },
          );
        }
      }
      return;
    }

    if (nodeName.toLowerCase().includes("apple")) {
      // Similar to sapphire, but with different properties
      if (material.name && material.name.toLowerCase().includes("watermelon")) {
        // material.metalness = 0.849;
        // material.roughness = 0.032; // Very smooth surface
        material.transparent = true;
        material.opacity = 0.5; // Slightly transparent like real gems
        // material.transmission = 0.01; // Some light passes through
        material.ior = 0.5; // Sapphire index of refraction
        material.thickness = 1; // Volume depth
        material.envMapIntensity = 1.2; // High reflection
      }
    }

    // Special handling for sapphire - ensure it's visible with proper gem-like properties
    if (nodeName.toLowerCase().includes("sapphire")) {
      const originalOpacity = material.opacity;
      const originalTransparent = material.transparent;

      // Fix materials that are invisible
      if (material.opacity < 0.3) {
        material.opacity = 1.0;
      }

      // Ensure material is not transparent if fully opaque
      if (material.opacity >= 1.0) {
        material.transparent = false;
      }

      // Apply gem-like material properties for sapphire
      if (material.name && material.name.toLowerCase().includes("gem")) {
        material.metalness = 0.849;
        material.roughness = 0.032; // Very smooth surface
        material.transparent = true;
        material.opacity = 0.85; // Slightly transparent like real gems
        material.transmission = 0; // Some light passes through
        material.ior = 0.5; // Sapphire index of refraction
        material.thickness = 1; // Volume depth
        material.envMapIntensity = 2.0; // High reflection
        material.clearcoat = 1.0; // Glossy coating
        material.clearcoatRoughness = 0.05; // Very smooth coating
        material.depthWrite = true;
      }

      // Ensure color is visible and not too dark
      if (material.color) {
        // Check if color is too dark (might appear invisible)
        const brightness =
          (material.color.r + material.color.g + material.color.b) / 3;
        if (DEBUG_MODE) {
          console.log(
            "[fixMaterialOpacity] Sapphire color brightness:",
            brightness,
            "RGB:",
            {
              r: material.color.r,
              g: material.color.g,
              b: material.color.b,
            },
          );
        }
      }

      material.needsUpdate = true;
      if (DEBUG_MODE) {
        console.log("[fixMaterialOpacity] Sapphire material:", nodeName, {
          name: material.name,
          opacityBefore: originalOpacity,
          opacityAfter: material.opacity,
          transparentBefore: originalTransparent,
          transparentAfter: material.transparent,
          metalness: material.metalness,
          roughness: material.roughness,
          envMapIntensity: material.envMapIntensity,
          hasMap: !!material.map,
          hasColor: !!material.color,
        });
      }
      return;
    }

    // Special handling for sage stones — translucent gem-like appearance
    // Strip texture alpha (same issue as quartz) so the full stone shape is visible.
    // Exclude spacer materials (e.g. "RUBY SPACER - GLASS") which share the same mesh
    // but should keep their original red glass appearance.
    const matNameLowerForSage = (material.name || "").toLowerCase();
    if (
      !matNameLowerForSage.includes("spacer") &&
      (matNameLowerForSage.includes("sage stone") ||
        (lowerName.includes("sage") && matNameLowerForSage.includes("sage")))
    ) {
      // Fill transparent areas of the texture with the gem's average color
      // so the entire bead surface shows the gem color (not black).
      if (node && material.map && material.map.image) {
        try {
          const img = material.map.image;
          const w = img.width || img.naturalWidth;
          const h = img.height || img.naturalHeight;
          if (w && h) {
            const canvas = document.createElement("canvas");
            canvas.width = w;
            canvas.height = h;
            const ctx = canvas.getContext("2d");
            ctx.drawImage(img, 0, 0, w, h);
            const imageData = ctx.getImageData(0, 0, w, h);
            const px = imageData.data;

            // Compute average color from visible (opaque) pixels
            let rSum = 0,
              gSum = 0,
              bSum = 0,
              count = 0;
            for (let i = 0; i < px.length; i += 4) {
              if (px[i + 3] > 128) {
                rSum += px[i];
                gSum += px[i + 1];
                bSum += px[i + 2];
                count++;
              }
            }
            const avgR = count ? Math.round(rSum / count) : 180;
            const avgG = count ? Math.round(gSum / count) : 200;
            const avgB = count ? Math.round(bSum / count) : 180;

            // Redraw: fill with average gem color first, then overlay the texture
            ctx.clearRect(0, 0, w, h);
            ctx.fillStyle = `rgb(${avgR},${avgG},${avgB})`;
            ctx.fillRect(0, 0, w, h);
            ctx.drawImage(img, 0, 0, w, h);

            // Force all alpha to 255 and lerp colors 80% toward white
            const finalData = ctx.getImageData(0, 0, w, h);
            const fp = finalData.data;
            for (let i = 3; i < fp.length; i += 4) {
              fp[i] = 255; // A
            }
            ctx.putImageData(finalData, 0, 0);

            const opaqueMap = new THREE.CanvasTexture(canvas);
            opaqueMap.encoding = material.map.encoding;
            opaqueMap.wrapS = material.map.wrapS;
            opaqueMap.wrapT = material.map.wrapT;
            opaqueMap.flipY = material.map.flipY;
            opaqueMap.needsUpdate = true;
            material.map = opaqueMap;

            if (DEBUG_MODE) {
              console.log(
                "[fixMaterialOpacity] Sage texture fill color:",
                `rgb(${avgR},${avgG},${avgB})`,
                "from",
                count,
                "visible pixels",
              );
            }
          }
        } catch (e) {
          if (DEBUG_MODE)
            console.warn(
              "[fixMaterialOpacity] Could not process sage texture:",
              e,
            );
        }
      }

      // Light glassy appearance — translucent with glossy surface + emissive glow
      material.transparent = true;
      material.opacity = 0.6;
      material.side = THREE.DoubleSide;
      material.depthWrite = false;
      material.roughness = 0.0;
      material.metalness = 0.0;
      material.envMapIntensity = 3.5;
      // Emissive adds a self-lit glow that brightens the gem colors without washing them out
      if (material.color) {
        material.emissive = material.color.clone().multiplyScalar(0.3);
      }
      if (material.clearcoat !== undefined) {
        material.clearcoat = 1.0;
        material.clearcoatRoughness = 0.0;
      }
      material.needsUpdate = true;
      if (DEBUG_MODE) {
        console.log(
          "[fixMaterialOpacity] Applied gem-like properties to sage stone:",
          nodeName,
          { materialName: material.name, opacity: material.opacity },
        );
      }
      return;
    }

    if (material.opacity < 0.3) {
      material.opacity = 1.0;
      material.transparent = false;
      material.depthWrite = true;
      material.alphaTest = 0;
      material.needsUpdate = true;
      if (DEBUG_MODE) {
        console.log(
          "[fixMaterialOpacity] Fixed invisible material (opacity was < 0.3):",
          nodeName,
          {
            materialName: material.name,
            hasMap: !!material.map,
            hasAlphaMap: !!material.alphaMap,
          },
        );
      }
    } else {
      if (
        nodeName.toLowerCase().includes("pearl")
        // || nodeName.toLowerCase().includes("mac")
      ) {
        // Clone material if we have access to the node and it hasn't been cloned yet
        // This prevents pearl material changes from affecting carabiners that share the material
        if (node && node.material && !node.material.userData.clonedForPearl) {
          node.material = node.material.clone();
          node.material.userData.clonedForPearl = true;
          material = node.material; // Update local reference
        }

        material.opacity = 1.0;
        // Apply correct pearl material properties
        material.metalness = 0.3;
        material.roughness = 0.3;
        material.envMapIntensity = 0.2;
        material.needsUpdate = true;
      }
    }

    material.needsUpdate = true;
  }

  function findRelatedMaterialNodes(
    formattedNodeId,
    spaceFormatNodeId,
    upperCaseFormatNodeId,
    upperCaseUnderscoreNodeId,
  ) {
    const materialNodes = [];

    // Build list of possible material node names with ALL formats
    // Note: GLTFLoader converts ALL spaces to underscores, so "NECKLACE - Black Rainbow SPACER"
    // becomes "NECKLACE_-_Black_Rainbow_SPACER" in the loaded model
    const possibleMaterialNames = [
      // Full underscore format (most common after GLTF load)
      `${formattedNodeId}_SPACER`,
      `${formattedNodeId}_SPACERS`,
      `${formattedNodeId}_Spacer`,
      `${formattedNodeId}_Spacers`,
      `${formattedNodeId}_Material`,
      `${formattedNodeId}_Materials`,
      `${formattedNodeId}_Inner`,
      `${formattedNodeId}_Beads`,
      `${formattedNodeId}_Inner_Beads`,
      // Mixed format with space before suffix
      `${formattedNodeId} SPACER`,
      `${formattedNodeId} SPACERS`,
      `${formattedNodeId} Spacer`,
      `${formattedNodeId} Spacers`,
      `${formattedNodeId} Material`,
      `${formattedNodeId} Materials`,
      // Space format variations
      `${spaceFormatNodeId} SPACER`,
      `${spaceFormatNodeId} SPACERS`,
      `${spaceFormatNodeId} Spacer`,
      `${spaceFormatNodeId} Spacers`,
      `${spaceFormatNodeId} Material`,
      `${spaceFormatNodeId} Materials`,
      `${spaceFormatNodeId} Inner`,
      `${spaceFormatNodeId} Beads`,
      // Uppercase format variations with spaces (for GLTF files with all caps)
      `${upperCaseFormatNodeId} SPACER`,
      `${upperCaseFormatNodeId} SPACERS`,
      `${upperCaseFormatNodeId} Material`,
      `${upperCaseFormatNodeId} Materials`,
      `${upperCaseFormatNodeId} Inner`,
      `${upperCaseFormatNodeId} Beads`,
      // Uppercase format variations with underscores (e.g., "NECKLACE_-_STELLA_SPACER")
      `${upperCaseUnderscoreNodeId}_SPACER`,
      `${upperCaseUnderscoreNodeId}_SPACERS`,
      `${upperCaseUnderscoreNodeId}_Material`,
      `${upperCaseUnderscoreNodeId}_Materials`,
      `${upperCaseUnderscoreNodeId}_Inner`,
      `${upperCaseUnderscoreNodeId}_Beads`,
    ];

    possibleMaterialNames.forEach((name) => {
      const node = window.dripModelViewer.model.getObjectByName(name);
      if (node) {
        materialNodes.push(node);
        if (DEBUG_MODE) {
          console.log("[findRelatedMaterialNodes] Found material node:", name);
        }
      }
    });

    if (DEBUG_MODE && materialNodes.length === 0) {
      console.warn(
        "[findRelatedMaterialNodes] No material nodes found for:",
        formattedNodeId,
      );
    }

    return materialNodes;
  }

  function handleInnerBeadsMaterial(componentType, nodeId) {
    if (componentType.toLowerCase() === "necklace") {
      const formattedNodeId = formatNodeName(nodeId);

      // Extract just the bead name from the original nodeId for GLTF pattern matching
      const beadName = nodeId.replace(/^necklace\s*-?\s*/i, "").trim();
      const capitalizedBeadName =
        beadName.charAt(0).toUpperCase() + beadName.slice(1);

      // Check for specific spacer nodes using multiple patterns
      const specificSpacerNames = [
        // Formatted pattern with underscores: NECKLACE_-_Pearl_SPACER
        `${formattedNodeId}_SPACER`,
        `${formattedNodeId}_SPACERS`,
        // Uppercase underscore pattern: NECKLACE_-_STELLA_SPACER (common in exported GLTF files)
        `NECKLACE_-_${beadName.toUpperCase()}_SPACER`,
        `NECKLACE_-_${beadName.toUpperCase()}_SPACERS`,
        // GLTF pattern with spaces: NECKLACE - Pearl SPACER
        `NECKLACE - ${capitalizedBeadName} SPACER`,
        `NECKLACE - ${capitalizedBeadName} SPACERS`,
        // Uppercase with spaces: NECKLACE - STELLA SPACER
        `NECKLACE - ${beadName.toUpperCase()} SPACER`,
        `NECKLACE - ${beadName.toUpperCase()} SPACERS`,
        // Handle multi-word spacer names like "BLACK RAINBOW"
        `NECKLACE - ${beadName} SPACER`,
        `NECKLACE - ${beadName} SPACERS`,
      ];

      let spacerFound = false;

      specificSpacerNames.forEach((spacerName) => {
        const spacerNode =
          window.dripModelViewer.model.getObjectByName(spacerName);
        if (spacerNode) {
          spacerNode.visible = true;
          spacerFound = true;

          if (spacerNode.material) {
            fixMaterialOpacity(
              spacerNode.material,
              spacerNode.name,
              spacerNode,
            );
          }

          spacerNode.traverse((child) => {
            child.visible = true;
            if (child.material) {
              fixMaterialOpacity(child.material, child.name, child);
            }
          });
        }
      });

      // If no specific spacer found, try generic inner beads
      if (!spacerFound) {
        const innerBeadsNode =
          window.dripModelViewer.model.getObjectByName("InnerBeads");
        if (innerBeadsNode) {
          innerBeadsNode.visible = true;

          if (innerBeadsNode.material) {
            fixMaterialOpacity(
              innerBeadsNode.material,
              innerBeadsNode.name,
              innerBeadsNode,
            );
          }

          innerBeadsNode.traverse((child) => {
            child.visible = true;
            if (child.material) {
              fixMaterialOpacity(child.material, child.name, child);
            }
          });
        }
      }

      const allNodes = getAllNodes(window.dripModelViewer.model);
      allNodes.forEach((node) => {
        const nodeName = node.name ? node.name.toLowerCase() : "";

        if (
          (nodeName.includes("bead") ||
            nodeName.includes("inner") ||
            nodeName.includes("material") ||
            nodeName.includes("spacer")) &&
          node.visible
        ) {
          if (node.material) {
            fixMaterialOpacity(node.material, node.name, node);
          }

          node.traverse((child) => {
            if (child.material) {
              fixMaterialOpacity(child.material, child.name, child);
            }
          });
        }
      });
    }
  }

  function formatNodeName(nodeName) {
    const parts = nodeName.split(" ");
    const componentType = parts[0].toLowerCase();

    const formattedParts = parts.map((part, index) => {
      if (part === "-") return "-";

      if (index === 0 && componentType === "necklace") {
        return part.toUpperCase();
      }

      return part.charAt(0).toUpperCase() + part.slice(1);
    });

    return formattedParts.join("_");
  }

  function hideAllNodesOfComponentType(componentType) {
    // Safety check: ensure model exists before trying to hide nodes
    if (!window.dripModelViewer || !window.dripModelViewer.model) {
      if (DEBUG_MODE) {
        console.warn("[hideAllNodesOfComponentType] Model not ready, skipping");
      }
      return;
    }

    const allNodes = getAllNodes(window.dripModelViewer.model);
    // Format: "necklace" -> "Necklace", but also check "NECKLACE" (all caps)
    const formattedComponentType =
      componentType.charAt(0).toUpperCase() + componentType.slice(1);
    const upperComponentType = componentType.toUpperCase();

    allNodes.forEach((node) => {
      if (node.name) {
        const name = node.name;

        // Special case: Handle NECKLACE_BEAN001 (sir's carabiner)
        if (name === "NECKLACE_BEAN001") {
          const currentNecklaceName =
            window.buildADripConfigurator?.currentOptions?.beads?.colorName?.toLowerCase() ||
            "";
          const isSirSelected = currentNecklaceName.includes("sir");

          // When hiding necklace nodes, only protect if sir is selected
          if (componentType === "necklace" && isSirSelected) {
            if (DEBUG_MODE) {
              console.log(
                "[hideAllNodesOfComponentType] Skipping NECKLACE_BEAN001 (sir's carabiner) - necklace mode",
              );
            }
            return;
          }

          // When hiding carabiner nodes, hide it unless sir is selected with gold
          if (componentType === "carabiner") {
            const currentCarabinerName =
              window.buildADripConfigurator?.currentOptions?.carabiner?.colorName?.toLowerCase() ||
              "";
            const isGoldCarabiner = currentCarabinerName.includes("gold");

            // Only keep visible if sir necklace + gold carabiner
            if (isSirSelected && isGoldCarabiner) {
              if (DEBUG_MODE) {
                console.log(
                  "[hideAllNodesOfComponentType] Skipping NECKLACE_BEAN001 (sir's carabiner) - carabiner mode",
                );
              }
              return;
            } else {
              // Hide it since sir is not selected or gold is not selected
              if (DEBUG_MODE) {
                console.log(
                  "[hideAllNodesOfComponentType] Hiding NECKLACE_BEAN001 - not sir+gold",
                );
              }
              node.visible = false;
              return;
            }
          }
        }

        // Check all possible patterns:
        // 1. Carabiner_-_ (title case with underscore)
        // 2. Carabiner - (title case with space)
        // 3. CARABINER_-_ (all caps with underscore)
        // 4. CARABINER - (all caps with space)
        let matches =
          name.startsWith(formattedComponentType + "_-_") ||
          name.startsWith(formattedComponentType + " - ") ||
          name.startsWith(upperComponentType + "_-_") ||
          name.startsWith(upperComponentType + " - ");

        // Special case: When hiding necklace nodes, also hide BEADS and Chain nodes
        if (componentType === "necklace") {
          matches =
            matches ||
            name.startsWith("BEADS_-_") ||
            name.startsWith("BEADS - ") ||
            name.startsWith("Beads_-_") ||
            name.startsWith("Beads - ") ||
            name.startsWith("Chain_-_") ||
            name.startsWith("Chain - ") ||
            name.startsWith("CHAIN_-_") ||
            name.startsWith("CHAIN - ");
        }

        if (matches) {
          node.visible = false;
        }
      }
    });
  }

  function getAllNodes(node) {
    const nodes = [];

    function traverse(currentNode) {
      nodes.push(currentNode);

      if (currentNode.children) {
        currentNode.children.forEach((child) => traverse(child));
      }
    }

    traverse(node);
    return nodes;
  }

  function findNodesByName(node, name, result) {
    if (node.name === name) {
      result.push(node);
    }

    if (node.children) {
      node.children.forEach((child) => {
        findNodesByName(child, name, result);
      });
    }
  }

  function setupAccordions() {
    const groups = document.querySelectorAll(".option-group");
    const headers = document.querySelectorAll(".option-group-header");

    if (groups.length === 0) return;

    const screenHeight = window.innerHeight;

    if (screenHeight >= 700) {
      groups.forEach((group) => {
        group.classList.add("active");
        const icon = group.querySelector(".accordion-toggle .icon");
        if (icon) icon.textContent = "−";
      });
      return;
    }

    // Screen height < 780px — enable accordion behavior
    groups.forEach((group, i) => {
      if (i === 0) {
        group.classList.add("active");
        const icon = group.querySelector(".accordion-toggle .icon");
        if (icon) icon.textContent = "−";
      } else {
        group.classList.remove("active");
        const icon = group.querySelector(".accordion-toggle .icon");
        if (icon) icon.textContent = "+";
      }
    });

    headers.forEach((header) => {
      header.addEventListener("click", () => {
        const currentGroup = header.closest(".option-group");

        groups.forEach((group) => {
          const icon = group.querySelector(".accordion-toggle .icon");
          if (group === currentGroup) {
            const isActive = group.classList.contains("active");
            group.classList.toggle("active");
            if (icon) icon.textContent = isActive ? "+" : "−";
          } else {
          }
        });
      });
    });
  }

  // Initialize the configurator immediately so selectors are visible
  if (!window.buildADripConfigurator) {
    window.buildADripConfigurator = new BuildADripConfigurator();
  }

  // Fallback: Check if add to cart button exists and add event listener if needed
  setTimeout(() => {
    const addToCartBtn = document.getElementById("add-to-cart");
    if (addToCartBtn && !addToCartBtn.hasAttribute("data-listener-added")) {
      if (DEBUG_MODE)
        console.log("Fallback: Adding event listener to add to cart button");
      addToCartBtn.addEventListener("click", () => {
        if (DEBUG_MODE) console.log("Fallback: Add to cart button clicked!");
        if (window.buildADripConfigurator) {
          window.buildADripConfigurator.addToCart();
        } else {
          console.error("buildADripConfigurator not available");
        }
      });
      addToCartBtn.setAttribute("data-listener-added", "true");
    }
  }, 1000);

  // Initialize 3D model viewer separately (this can take time)
  if (container && !window.dripModelViewer) {
    // Use only the base v12 model
    const modelUrl = "v12.glb";

    // Set up event listener BEFORE creating the viewer to avoid race condition
    let modelLoadHandled = false;
    window.addEventListener(
      "model-loaded",
      () => {
        if (modelLoadHandled) return; // Already handled
        modelLoadHandled = true;

        try {
          window.buildADripConfigurator?.updateModel();
        } catch (error) {
          console.error(
            "[initBuildADrip] Error in updateModel (event):",
            error,
          );
        } finally {
          // Always hide loading overlay when model is loaded
          if (modelLoader) {
            modelLoader.style.display = "none";
          }
        }
      },
      { once: true },
    );

    window.dripModelViewer = new DripModelViewer(
      "build-a-drip-model-viewer",
      modelUrl,
    );

    // After viewer creation, check if model already loaded (cached case)
    setTimeout(() => {
      if (window.dripModelViewer?.model && !modelLoadHandled) {
        modelLoadHandled = true;
        try {
          window.buildADripConfigurator?.updateModel();
        } catch (error) {
          console.error(
            "[initBuildADrip] Error in updateModel (already loaded):",
            error,
          );
        } finally {
          if (modelLoader) {
            modelLoader.style.display = "none";
          }
        }
      }
    }, 100);
  }
}

// Initialize when DOM is ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initBuildADrip);
} else {
  initBuildADrip();
}
